import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  CircularProgress,
  Alert,
  Chip,
  alpha,
} from '@mui/material';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  Event as EventIcon,
  CheckCircle as CheckCircleIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  CardGiftcard as GiftIcon,
} from '@mui/icons-material';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('/dashboard/stats/current-month');
      setStats(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load dashboard stats');
      console.error('Error loading stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const StatCard = ({ title, value, icon: Icon, color = 'primary', subtitle = null, formatValue = (v) => v }) => {
    return (
      <Card sx={{ transition: 'transform 0.2s ease, box-shadow 0.2s ease', '&:hover': { transform: 'translateY(-2px)' } }}>
        <CardContent sx={{ p: 2.5 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <Icon color={color} sx={{ mr: 2, fontSize: 36 }} />
            <Box flex={1}>
              <Typography variant="h6" color="text.secondary">
                {title}
              </Typography>
              <Typography variant="h4" color={`${color}.main`}>
                {formatValue(value)}
              </Typography>
              {subtitle && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Layout>
      <Container maxWidth={false} sx={{ width: '100%' }}>
        <Typography variant="h4" gutterBottom fontWeight={700}>
          Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Welcome back, {user?.full_name || user?.email}!
        </Typography>

        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && stats && (
          <>
            {/* Data error/warning messages at the top */}
            {stats.data_error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {stats.data_error}
              </Alert>
            )}
            {stats.data_freshness_warning && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {stats.data_freshness_warning}
              </Alert>
            )}

            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Last updated: {formatDate(stats.last_updated)}
              </Typography>
              {stats.last_updated && (
                <Chip
                  label="Live Data"
                  color="success"
                  size="small"
                  sx={{ height: 20 }}
                />
              )}
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  title="Completed Tours"
                  value={stats.completed_tours}
                  icon={CheckCircleIcon}
                  color="success"
                  subtitle="This month to date"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  title="Total Guests"
                  value={stats.total_guests}
                  icon={PeopleIcon}
                  color="info"
                  subtitle="This month to date"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  title="Tour Sales Revenue (Gross)"
                  value={stats.total_revenue_gross}
                  icon={MoneyIcon}
                  color="success"
                  subtitle="Including GST - This month to date"
                  formatValue={formatCurrency}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  title="Tour Sales Revenue (Net)"
                  value={stats.total_revenue_net}
                  icon={MoneyIcon}
                  color="info"
                  subtitle="Excluding GST - This month to date"
                  formatValue={formatCurrency}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  title="Gift Voucher Sales"
                  value={stats.gift_voucher_sales}
                  icon={GiftIcon}
                  color="secondary"
                  subtitle="This month"
                  formatValue={formatCurrency}
                />
              </Grid>
            </Grid>

            {/* Occupancy rates */}
            {stats.tour_occupancy_rates && Object.keys(stats.tour_occupancy_rates).length > 0 && (
              <Card sx={{ mt: 3 }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="h6" gutterBottom fontWeight={600}>
                    Occupancy Rates
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    * Assumes 12 seats per tour when capacity data is not available
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    {Object.entries(stats.tour_occupancy_rates).map(([tourName, rateData]) => {
                      // Handle both old format (just number) and new format (object)
                      const isObject = typeof rateData === 'object' && rateData !== null;
                      const rate = isObject ? rateData.rate : rateData;
                      const tours = isObject ? rateData.tours : null;
                      const guests = isObject ? rateData.guests : null;
                      
                      return (
                        <Grid item xs={12} sm={6} md={4} key={tourName}>
                          <Box
                            sx={(theme) => ({
                              p: 2,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 2,
                              backgroundColor: tourName === 'Overall'
                                ? alpha(theme.palette.primary.main, 0.08)
                                : 'transparent',
                            })}
                          >
                            <Typography variant="body2" color="text.secondary">
                              {tourName}
                            </Typography>
                            <Typography variant="h6" color="primary" sx={{ mb: 0.5 }}>
                              {rate !== null && rate !== undefined ? `${rate}%` : 'N/A'}
                            </Typography>
                            {tours !== null && guests !== null && (
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                {tours} {tours === 1 ? 'tour' : 'tours'}, {guests} PAX
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </Container>
    </Layout>
  );
};

export default Dashboard;
